import { useState } from "react";
import { FaTrashAlt } from "react-icons/fa";
import { Form, FormActions, FormInput, FormDate } from "../Controls/Form";
import ListIconButton from "../Controls/ListIconButton";
import SkillSearch from "../Controls/SkillSearch";
import { toast } from "react-toastify";

function emptyClass() {
  return {
    ID: 0,
    Name: "",
    Grade: "",
    Number: "",
    TaughtBy: "",
    StartDate: null,
    EndDate: null,
    SkillIDs: [],
    Skills: [],
  };
}

/**
 * Education class form body. Prefer EducationClassEditModal.
 * @param {object|null} initialClass
 * @param {(cls: object) => void} onSave
 * @param {() => void} onClose
 */
export default function EducationClassEdit({ initialClass = null, onSave, onClose }) {
  const [tab, setTab] = useState("info");
  const [cls, setCls] = useState(() => {
    if (!initialClass) return emptyClass();
    const skillIds = initialClass.SkillIDs || initialClass.skillIDs || [];
    const skills =
      initialClass.Skills ||
      initialClass.skills ||
      skillIds.map((id) => ({
        SkillID: id,
        SkillName: `Skill #${id}`,
      }));
    return {
      ID: initialClass.ID || initialClass.id || 0,
      Name: initialClass.Name || initialClass.name || "",
      Grade: initialClass.Grade || initialClass.grade || "",
      Number: initialClass.Number || initialClass.number || "",
      TaughtBy: initialClass.TaughtBy || initialClass.taughtBy || "",
      StartDate: initialClass.StartDate || initialClass.startDate || null,
      EndDate: initialClass.EndDate || initialClass.endDate || null,
      SkillIDs: skillIds.length ? skillIds : skills.map((s) => s.SkillID || s.skillID).filter(Boolean),
      Skills: skills,
    };
  });

  function close() {
    onClose?.();
  }

  function onSkillSelect(skill) {
    const sid = Number(skill?.ID ?? skill?.id ?? 0) || 0;
    if (!sid) return;
    const name = skill?.Name ?? skill?.name ?? `Skill #${sid}`;
    setCls((prev) => {
      const existing = prev.Skills || [];
      if (existing.some((s) => Number(s.SkillID || s.skillID) === sid)) return prev;
      const nextSkills = [...existing, { SkillID: sid, SkillName: name }];
      return {
        ...prev,
        Skills: nextSkills,
        SkillIDs: nextSkills.map((s) => s.SkillID || s.skillID),
      };
    });
  }

  function removeSkill(skillId) {
    setCls((prev) => {
      const nextSkills = (prev.Skills || []).filter(
        (s) => Number(s.SkillID || s.skillID) !== Number(skillId)
      );
      return {
        ...prev,
        Skills: nextSkills,
        SkillIDs: nextSkills.map((s) => s.SkillID || s.skillID),
      };
    });
  }

  function onDone() {
    const name = (cls.Name || "").trim();
    if (!name) {
      toast.error("Class name is required");
      setTab("info");
      return;
    }
    onSave?.({
      ID: cls.ID || 0,
      Name: name,
      Grade: (cls.Grade || "").trim() || null,
      Number: (cls.Number || "").trim(),
      TaughtBy: (cls.TaughtBy || "").trim() || null,
      StartDate: cls.StartDate || null,
      EndDate: cls.EndDate || null,
      SkillIDs: (cls.SkillIDs || []).filter((id) => Number(id) > 0),
      Skills: cls.Skills || [],
    });
    close();
  }

  const skills = cls.Skills || [];
  const skillCount = skills.length;

  return (
    <div className="cm-modal-embedded">
      <Form className="cm-modal-form" object={cls} onUpdate={setCls}>
        <div className="cm-modal-tabs" role="tablist" aria-label="Class sections">
          <button
            type="button"
            role="tab"
            id="class-tab-info"
            aria-selected={tab === "info"}
            aria-controls="class-panel-info"
            className={tab === "info" ? "is-active" : undefined}
            data-testid="class-tab-info"
            onClick={() => setTab("info")}
          >
            Info
          </button>
          <button
            type="button"
            role="tab"
            id="class-tab-dates"
            aria-selected={tab === "dates"}
            aria-controls="class-panel-dates"
            className={tab === "dates" ? "is-active" : undefined}
            data-testid="class-tab-dates"
            onClick={() => setTab("dates")}
          >
            Dates
          </button>
          <button
            type="button"
            role="tab"
            id="class-tab-skills"
            aria-selected={tab === "skills"}
            aria-controls="class-panel-skills"
            className={tab === "skills" ? "is-active" : undefined}
            data-testid="class-tab-skills"
            onClick={() => setTab("skills")}
          >
            Skills{skillCount > 0 ? ` (${skillCount})` : ""}
          </button>
        </div>

        <div className="cm-modal-tab-panels">
          <div
            id="class-panel-info"
            role="tabpanel"
            aria-labelledby="class-tab-info"
            aria-hidden={tab !== "info"}
            className={`cm-modal-tab-panel${tab === "info" ? " is-active" : ""}`}
            {...(tab !== "info" ? { inert: "" } : {})}
          >
            <FormInput label="Name" field="Name" />
            <FormInput label="Grade" field="Grade" />
            <FormInput label="Number" field="Number" />
            <FormInput label="Taught By" field="TaughtBy" />
          </div>

          <div
            id="class-panel-dates"
            role="tabpanel"
            aria-labelledby="class-tab-dates"
            aria-hidden={tab !== "dates"}
            className={`cm-modal-tab-panel${tab === "dates" ? " is-active" : ""}`}
            {...(tab !== "dates" ? { inert: "" } : {})}
          >
            <FormDate label="Start Date" field="StartDate" nullable />
            <FormDate label="End Date" field="EndDate" nullable />
          </div>

          <div
            id="class-panel-skills"
            role="tabpanel"
            aria-labelledby="class-tab-skills"
            aria-hidden={tab !== "skills"}
            className={`cm-modal-tab-panel${tab === "skills" ? " is-active" : ""}`}
            data-testid="education-class-skills"
            {...(tab !== "skills" ? { inert: "" } : {})}
          >
            <p className="fieldHint">Skills taught or practiced in this class.</p>
            <SkillSearch label="Add skill" onSelect={onSkillSelect} />
            {skills.length === 0 ? (
              <p className="fieldHint">No skills added yet.</p>
            ) : (
              <ul className="education-class-skill-list">
                {skills.map((s) => {
                  const sid = s.SkillID || s.skillID;
                  const sname = s.SkillName || s.skillName || `Skill #${sid}`;
                  return (
                    <li key={sid} className="education-class-skill-item">
                      <span className="education-class-skill-item__name">{sname}</span>
                      <ListIconButton
                        aria-label={`Remove ${sname}`}
                        title="Remove skill"
                        data-testid={`education-class-skill-remove-${sid}`}
                        onClick={() => removeSkill(sid)}
                      >
                        <FaTrashAlt aria-hidden />
                      </ListIconButton>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <FormActions
          doneLabel="Done"
          onSaveAsync={async () => {
            onDone();
          }}
          onCancelAsync={async () => {
            close();
          }}
        />
      </Form>
    </div>
  );
}
